import Vue from 'vue'

import {
  associateUser,
  updateConsumer,
  createOpportunity,
  getOpportunity
} from './queries'
import { request } from '~/api'

const OPPORTUNITY_ID_CACHE = 'MAX.opportunity'

let ready
const pending = new Promise(resolve => { ready = resolve })

const state = () => ({
  data: {},
  accessToken: null,
  instance: '',
  platform: '',
  viewOnly: false
})

const getters = {
  pending: () => pending,
  modules (state, getters) {
    return new Set(Object.keys(getters).filter(key => key.includes('/')).map(key => key.split('/')[0]))
  },
  context: state => ({
    opportunity: state.data.id,
    instance: state.instance,
    platform: state.platform
  }),
  consumer: state => state.data.consumer || {},
  profile: (state, getters) => field => getters.consumer.profile?.[field]
}

const actions = {
  /**
   * Associate a user with their Opportunity
   * @param {String} consumer - Auth0 UID
   * @return {Promise}
   */
  async associateUser ({ commit, dispatch, state }, consumer) {
    await pending
    const { data: gql } = await dispatch('mutate', {
      mutation: associateUser,
      input: { consumer }
    })
    if (!gql) return
    commit('setOpportunity', gql.associateUserWithOpportunity.opportunity)
  },

  /**
   * Clear an Opportunity from both Vuex and local storage, typically used when
   * a user logs out to prevent users on a shared computer from affecting a
   * logged-out. A new Opportunity will be created so the site continues to
   * function if the browser window is left open.
   * @return {Promise}
   */
  clear ({ commit, dispatch }) {
    commit('setOpportunity')
    localStorage.removeItem(OPPORTUNITY_ID_CACHE)
    return dispatch('create')
  },

  /**
   * Create a new Opportunity and persist it's value to state.
   * @return {Promise}
   */
  async create ({ commit, getters, state }) {
    const { instance, platform } = state
    if (!instance || !platform) {
      throw new Error('instance and platform required, must `setContext` first')
    }

    const { data: gql } = await request(createOpportunity, { instance, platform })
    commit('setOpportunity', gql.createOpportunity.opportunity)
    commit('setAccessToken')
  },

  /**
   * Get an existing opportunity. If an ID is not provided, an Opportunity ID will
   * be read from local storage. If no ID is found either as a parameter or in
   * local storage, a new Opportunity is created instead.
   * @param {String} id - The Opportunity handle (optional)
   */
  async get ({ commit, dispatch }, id) {
    id = id || localStorage.getItem(OPPORTUNITY_ID_CACHE)
    if (!id) return dispatch('create')

    const { data: gql } = await request(getOpportunity, { id })
    if (!gql.opportunity) return dispatch('create')

    commit('setOpportunity', gql.opportunity)
  },

  /**
   * Mutate the Opportunity through a GraphQL request. This method respects the
   * `viewOnly` state and should be used in place of a standalone query.
   * @param {String} mutation - The body of the GraphQL mutation.
   * @param {Object} input - Variables for the `input` mutation parameter. The
   * `context` parameter is automatically filled based on the local state.
   * @return {Promise}
   */
  async mutate ({ getters, state }, { mutation, input, id }) {
    if (state.viewOnly) return {}
    return request(mutation, { context: getters.context, input, consumer: getters.consumer.profile, id })
  },

  /**
   * Query additional information about the Opportunity. This can either be
   * used in a local component or cached back on the state object by using
   * the `update` action.
   * @param {String} query - The body of the GraphQL query. The current
   * Opportunity ID will be passed as an input parameter.
   */
  async query ({ state }, query) {
    if (!state.data.id) return
    const { data: { opportunity } } = await request(query, { id: state.data.id })
    return opportunity
  },

  /**
   * When necessary the local state of the Opportunity can be updated. This will
   * not persist to API and is only recommended to keep canonical data in sync.
   * @param {Object} data - Shallow merged with existing state object.
   */
  update ({ commit, state }, data = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Not persisting to the Opportunity record!')
    }
    commit('setOpportunity', { ...state.data, ...data })
  },

  /**
   * Updates the opportunity consumer data.
   * @param {Object} consumer - consumer data
   */
  async updateConsumer ({ commit, state, dispatch }, consumer = {}) {
    if (state.viewOnly) return
    const { data: gql } = await request(updateConsumer, {
      context: {
        instance: state.instance,
        platform: state.platform
      },
      input: {
        consumer: {
          id: consumer.id,
          firstName: consumer.profile.firstName,
          lastName: consumer.profile.lastName,
          address: {
            locality: consumer.profile.address?.locality || '',
            postalCode: consumer.profile.address?.postalCode || '',
            region: consumer.profile.address?.region || '',
            street: consumer.profile.address?.street || ''
          },
          phone: consumer.profile.phone
        }
      }
    })

    if (!gql?.updateConsumer) return
    commit('setConsumer', gql.updateConsumer.profile)
  }
}

const mutations = {
  setContext (state, { instance, platform }) {
    state.instance = instance
    state.platform = platform
  },
  setOpportunity: (state, data = {}) => {
    if (data.id && !state.viewOnly) {
      localStorage.setItem(OPPORTUNITY_ID_CACHE, data.id)
    }
    state.data = data
    ready()
  },
  setAccessToken: (state, accessToken = null) => {
    state.accessToken = accessToken
  },
  setViewOnly (state, viewOnly) {
    state.viewOnly = viewOnly
  },
  /**
   * Only profile fields can be updated once an Opportunity has been loaded.
   * @param {Object} profile
   */
  setConsumer (state, profile) {
    Vue.set(state.data, 'consumer', {
      ...state.data.consumer,
      profile
    })
  }
}

export const store = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
